// T2-2 — THE CONSENT LAW, IN ONE PLACE.
//
// A member agrees to the Terms and the Privacy Policy BEFORE her room is built, the agreement is
// RECORDED on her row (version + timestamp), and the server refuses a room-create that carries no
// consent. The checkbox in the form PREVENTS; this module ENFORCES — the same shape as the gate law.
//
// It is plain JS on purpose: the server imports it (node ESM) and the client imports it (Vite), so
// the version the form promises and the version the server records can never drift.

// Bump this whenever terms-of-service.md or privacy-policy.md changes in substance. The value is
// written onto the member's row at signup, so the row says WHICH document she agreed to.
// >>> **TERMS-2 (her order, 2026-09-03): BUMPED FOR THE DISCRETIONARY-REMOVAL CLAUSE (§10.1).** <<<
// Her text, in the termination section, in clean legal register, with the attorney-review note beside
// it in the document itself.
//
// **AND `TERMS_RECONSENT_VERSION` BELOW STAYS EMPTY, WHICH IS R2's OWN RULING AND MATTERS MORE HERE
// THAN IT DID.** A bump alone asks nobody to re-agree: `reconsentRequired` returns false while that
// constant is empty, so no existing member is stopped, prompted or logged out by this change. The
// day the attorney signs off is the day she sets it, and that is HER act rather than a side effect
// of a version string moving.
// >>> **BUMPED 2026-09-08 FOR THE ENTERTAINMENT AND GUIDANCE CLAUSE (§11.1), her order.** <<< The
// clause from the marketplace legal package, added to the live Terms: entertainment and
// self-reflection framing, the not-a-substitute-for-professional-advice sentence, the "as is" and
// no-guarantee acknowledgements, and the eighteen-and-over requirement.
//
// >>> **AND `TERMS_RECONSENT_VERSION` BELOW IS STILL EMPTY, WHICH IS DELIBERATE AND IS NOT AN
// OVERSIGHT.** <<< Her instruction was *"bump TERMS_VERSION for re-consent"*, and a bump alone asks
// nobody to re-agree — R2's standing ruling, written directly below, is that setting the re-consent
// constant is HER act on the day the attorney signs off, never a side effect of a version string
// moving. **The clause is a TEMPLATE PENDING ATTORNEY REVIEW**, and prompting several hundred
// existing members to affirmatively re-accept un-reviewed terms is the one thing that would be
// worse than not prompting them at all. New signups record this version from today; existing
// members are asked the day she says so, which is one line.
// >>> **BUMPED 2026-09-30 FOR R-TERMS (B1-Q1 YES):** the line that closed the Read for 12th House page
// ("Delivered reading time is non-refundable; unused minutes are returned automatically") now lives in §4.
// Re-consent stays hers to trigger, exactly as above. <<<
export const TERMS_VERSION = "2026-09-30";

// R2 (owner ruling, 2026-07-29) — ONE-TIME RE-CONSENT FOR EXISTING MEMBERS.
// Built now, TRIGGERED ONLY when the owner declares the attorney-final Terms: set this to that
// version string (normally the same value as TERMS_VERSION above) and every member whose recorded
// consent is not that version is asked once, at her next sign-in. EMPTY = nobody is ever prompted,
// which is the state the owner asked to ship in.
export const TERMS_RECONSENT_VERSION = "";

// The exact sentence beside the checkbox. One definition, so the evidence capture, the form and any
// future surface all say the same thing.
export const CONSENT_TEXT = "I have read and agree to the Terms of Service and Privacy Policy";

export function normalizeVersion(value) {
  return typeof value === "string" ? value.trim() : "";
}

// Has this member's consent to the CURRENT terms already been recorded?
export function consentOnRecord(recordedVersion) {
  return normalizeVersion(recordedVersion) === TERMS_VERSION;
}

// THE SERVER GATE. Returns the reasons a request must be refused; empty means it may proceed.
// `accepted`/`version` come from the request body; `recordedVersion` from the member's row.
export function consentViolations({ accepted, version, recordedVersion } = {}) {
  if (consentOnRecord(recordedVersion)) return [];
  const submitted = normalizeVersion(version);
  if (accepted !== true) return ["no consent to the Terms was submitted, and none is on record"];
  if (submitted !== TERMS_VERSION) {
    return [`consent names terms version "${submitted || "(none)"}", the current version is "${TERMS_VERSION}"`];
  }
  return [];
}

// R2. True only once the owner has declared a re-consent version AND this member has not agreed to it.
export function reconsentRequired(recordedVersion) {
  const target = normalizeVersion(TERMS_RECONSENT_VERSION);
  if (!target) return false;
  return normalizeVersion(recordedVersion) !== target;
}
